import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import BlurBg from "../../../assets/ImageEditing/images/api-blur-bg.jpg";
import Crop from "../../../assets/ImageEditing/images/api-crop-image.jpg";
import ImageToPSD from "../../../assets/ImageEditing/images/api-delayer-image.jpg";
import EraseForeground from "../../../assets/ImageEditing/images/api-erase-foreground.jpg";
import ExpandImage from "../../../assets/ImageEditing/images/api-expand-image.jpg";
import MaskGenerator from "../../../assets/ImageEditing/images/api-masks-generator.jpg";
import RemoveBg from "../../../assets/ImageEditing/images/api-remove-bg.jpg";
import ReplaceBg from "../../../assets/ImageEditing/images/api-replace-bg.jpg";
import { ConsoleAPICardProps } from "../../../components/Home/API/Cards/Card/ConsoleAPICard";
import ConsoleAPICards from "../../../components/Home/API/Cards/ConsoleAPICards";
import ConsoleAPILayout from "../../../components/Home/API/ConsoleAPILayout";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import LinkConstants from "../../../constants/LinkConstants";
import RouterConstants from "../../../constants/RouterConstants";

const BackgroundComposition = () => {
	const { t } = useTranslation("translation", { keyPrefix: "imageEditing.api" });
	const navigate = useNavigate();

	const rightActions = (
		<>
			<BriaButton buttonType="secondarySmall" onClick={() => navigate(RouterConstants.ACCOUNT_IFRAMES.fullPath)}>
				{t("headerButtons.configureIframe")}
			</BriaButton>
			<BriaButton
				buttonType="secondarySmall"
				onClick={() => navigate(RouterConstants.IMAGE_EDITING.IMAGE.DEMO.fullPath)}
			>
				{t("headerButtons.playground")}
			</BriaButton>
		</>
	);

	const openDocs = (link: string) => window.open(link, "_blank");

	const backgroundCompositionCards: ConsoleAPICardProps[] = [
		{
			image: ReplaceBg,
			header: t("cards.replaceBg.header"),
			description: t("cards.replaceBg.description"),
			buttons: (
				<>
					<BriaButton
						buttonType="textSmall"
						onClick={() => openDocs(LinkConstants.IMAGE_EDITING.REPLACE_BACKGROUND.DOCS)}
					>
						{t("cards.replaceBg.primaryBtn")}
					</BriaButton>
					<BriaButton
						buttonType="textSmall"
						onClick={() =>
							window.open(LinkConstants.IMAGE_EDITING.REPLACE_BACKGROUND.COMFY_UI.NODE, "_blank")
						}
					>
						{t("common.comfyNodeBtn")}
					</BriaButton>
				</>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.REPLACE_BACKGROUND.DOCS),
		},
		{
			image: RemoveBg,
			header: t("cards.removeBg.header"),
			description: t("cards.removeBg.description"),
			buttons: (
				<>
					<BriaButton
						buttonType="textSmall"
						onClick={() => window.open(LinkConstants.BRIA_NEW_PLATFORM_BACKGROUND_REMOVE_SANDBOX, "_blank")}
					>
						{t("cards.removeBg.tryInSandboxBtn")}
					</BriaButton>
					<BriaButton
						buttonType="textSmall"
						onClick={() => openDocs(LinkConstants.IMAGE_EDITING.REMOVE_BACKGROUND.DOCS)}
					>
						{t("cards.removeBg.primaryBtn")}
					</BriaButton>
					<BriaButton
						buttonType="textSmall"
						onClick={() =>
							window.open(LinkConstants.IMAGE_EDITING.REMOVE_BACKGROUND.COMFY_UI.NODE, "_blank")
						}
					>
						{t("common.comfyNodeBtn")}
					</BriaButton>
				</>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.REMOVE_BACKGROUND.DOCS),
		},
		{
			image: BlurBg,
			header: t("cards.blurBg.header"),
			description: t("cards.blurBg.description"),
			buttons: (
				<BriaButton
					buttonType="textSmall"
					onClick={() => openDocs(LinkConstants.IMAGE_EDITING.BLUR_BACKGROUND.DOCS)}
				>
					{t("cards.blurBg.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.BLUR_BACKGROUND.DOCS),
		},
		{
			image: ExpandImage,
			header: t("cards.expandImage.header"),
			description: t("cards.expandImage.description"),
			buttons: (
				<>
					<BriaButton
						buttonType="textSmall"
						onClick={() => openDocs(LinkConstants.IMAGE_EDITING.EXPAND_IMAGE.DOCS)}
					>
						{t("cards.expandImage.primaryBtn")}
					</BriaButton>
					<BriaButton
						buttonType="textSmall"
						onClick={() => window.open(LinkConstants.IMAGE_EDITING.EXPAND_IMAGE.COMFY_UI.NODE, "_blank")}
					>
						{t("common.comfyNodeBtn")}
					</BriaButton>
				</>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.EXPAND_IMAGE.DOCS),
		},
		{
			image: ImageToPSD,
			header: t("cards.imageToPSD.header"),
			description: t("cards.imageToPSD.description"),
			buttons: (
				<BriaButton
					buttonType="textSmall"
					onClick={() => openDocs(LinkConstants.IMAGE_EDITING.IMAGE_TO_PSD.DOCS)}
				>
					{t("cards.imageToPSD.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.IMAGE_TO_PSD.DOCS),
		},
		{
			image: MaskGenerator,
			header: t("cards.maskGenerator.header"),
			description: t("cards.maskGenerator.description"),
			buttons: (
				<BriaButton
					buttonType="textSmall"
					onClick={() => openDocs(LinkConstants.IMAGE_EDITING.MASK_GENERATOR.DOCS)}
				>
					{t("cards.maskGenerator.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.MASK_GENERATOR.DOCS),
		},
		{
			image: EraseForeground,
			header: t("cards.eraseForeground.header"),
			description: t("cards.eraseForeground.description"),
			buttons: (
				<>
					<BriaButton
						buttonType="textSmall"
						onClick={() => openDocs(LinkConstants.IMAGE_EDITING.ERASE_FOREGROUND.DOCS)}
					>
						{t("cards.eraseForeground.primaryBtn")}
					</BriaButton>
					<BriaButton
						buttonType="textSmall"
						onClick={() =>
							window.open(LinkConstants.IMAGE_EDITING.ERASE_FOREGROUND.COMFY_UI.NODE, "_blank")
						}
					>
						{t("common.comfyNodeBtn")}
					</BriaButton>
				</>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.ERASE_FOREGROUND.DOCS),
		},
		{
			image: Crop,
			header: t("cards.crop.header"),
			description: t("cards.crop.description"),
			buttons: (
				<BriaButton buttonType="textSmall" onClick={() => openDocs(LinkConstants.IMAGE_EDITING.CROP.DOCS)}>
					{t("cards.crop.primaryBtn")}
				</BriaButton>
			),
			onClick: () => openDocs(LinkConstants.IMAGE_EDITING.CROP.DOCS),
		},
	];

	return (
		<ConsoleAPILayout rightActions={rightActions} showIntegrationKeyButton>
			<ConsoleAPICards cards={backgroundCompositionCards} />
		</ConsoleAPILayout>
	);
};

export default observer(BackgroundComposition);
